import {
  Box,
  CircularProgress,
  HStack,
  Icon,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Tag,
  Text,
  useToast,
  VStack,
} from "@chakra-ui/react";
import {
  ArrowTopRightOnSquareIcon,
  EllipsisVerticalIcon,
} from "@heroicons/react/24/outline";
import { useUpdateEffect, useWindowsFocus } from "@reactuses/core";
import dayjs from "dayjs";
import { Fragment, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../components/chackra-ui-button";
import {
  useTaxInvoicePdfUrlLazyQuery,
  useTaxInvoicesQuery,
} from "../../graphql/generated/user-v2-hooks";
import {
  mapInvoiceTypeToBadgeColor,
  mapInvoiceTypeToText,
  isSimplified,
} from "../../helpers/tax-invoice";
import { formatPrice } from "../../helpers/format-price";
import { openExternalUrl } from "../../helpers/open-external-url";
import { RequestInvoiceModal } from "./request-invoice-modal";

export const InvoiceSection = () => {
  const focused = useWindowsFocus();
  const { t } = useTranslation();
  const toast = useToast();
  const [fetchMoreLoading, setFetchMoreLoading] = useState(false);
  const [loadingPdfId, setLoadingPdfId] = useState<string | null>(null);
  const [confirmInvoiceId, setConfirmInvoiceId] = useState<string | null>(null);

  const { data, loading, fetchMore, refetch } = useTaxInvoicesQuery({
    variables: { limit: 5 },
    fetchPolicy: "network-only",
    context: { endpoint: "fitnextv2" },
    onError: (error) => {
      toast({
        title: error.message,
        status: "error",
        isClosable: true,
      });
    },
  });

  const [fetchPdfUrl] = useTaxInvoicePdfUrlLazyQuery({
    context: { endpoint: "fitnextv2" },
    fetchPolicy: "network-only",
  });

  useUpdateEffect(() => {
    if (focused) {
      refetch();
    }
  }, [focused]);

  const invoices = data?.taxInvoices.items ?? [];
  const hasMore = data?.taxInvoices.hasMore ?? false;

  const openPdf = async (invoiceId: string) => {
    setLoadingPdfId(invoiceId);
    try {
      const result = await fetchPdfUrl({ variables: { invoiceId } });
      const url = result.data?.taxInvoicePdfUrl;

      if (!url) {
        toast({
          title: t("taxInvoices.pdfUnavailable"),
          status: "info",
          isClosable: true,
        });
        return;
      }

      openExternalUrl(url);
    } catch {
      toast({
        title: t("taxInvoices.pdfError"),
        status: "error",
        isClosable: true,
      });
    } finally {
      setLoadingPdfId(null);
    }
  };

  const canUpgrade = (invoice: { invoiceType: string }) =>
    isSimplified(invoice.invoiceType);

  return (
    <Box w="full">
      <Text
        fontSize="20px"
        fontWeight={600}
        fontFamily="'Barlow Semi Condensed', sans-serif"
        color="#13192E"
        mb={3}
        px={1}
      >
        {t("taxInvoices.title")}
      </Text>
      <Box
        bg="white"
        borderWidth="1px"
        borderColor="slate.100"
        borderRadius="12px"
        p={5}
      >
      <VStack w="full" spacing={0} align="start">
        {loading || !data ? (
          <HStack py={2}>
            <CircularProgress isIndeterminate color="blue.500" size={4} />
            <Text>{t("taxInvoices.loading")}</Text>
          </HStack>
        ) : invoices.length > 0 ? (
          <VStack w="full" spacing={0}>
            {invoices.map((invoice, index) => (
              <Fragment key={invoice.id}>
                {index > 0 && (
                  <Box w="full" borderTopWidth="1px" borderTopColor="slate.100" />
                )}
                <HStack w="full" justify="space-between" align="center" py={3}>
                  <VStack align="start" spacing={1}>
                    <Text fontWeight={600} fontSize="14px" color="#13192E">
                      {dayjs(invoice.createdAt).format("DD/MM/YYYY")}
                    </Text>
                    <HStack spacing={1.5}>
                      <Tag
                        colorScheme={mapInvoiceTypeToBadgeColor(
                          invoice.invoiceType,
                        )}
                        size="sm"
                      >
                        {mapInvoiceTypeToText(t, invoice.invoiceType)}
                      </Tag>
                      <Text fontSize="11px" color="slate.400">
                        {invoice.number}
                      </Text>
                    </HStack>
                  </VStack>
                  <HStack spacing={2} flexShrink={0}>
                    <Text
                      fontFamily="'Barlow Condensed', sans-serif"
                      fontWeight={700}
                      fontSize="18px"
                      color="#13192E"
                      whiteSpace="nowrap"
                    >
                      {formatPrice({ t, amount: invoice.total })}
                    </Text>
                    <Menu>
                      <MenuButton
                        as={IconButton}
                        icon={
                          <Icon boxSize={5} color="slate.400">
                            <EllipsisVerticalIcon />
                          </Icon>
                        }
                        variant="ghost"
                        aria-label={t("taxInvoices.actions")}
                        isLoading={loadingPdfId === invoice.id}
                        minW="40px"
                        h="40px"
                        borderRadius="10px"
                      />
                      <MenuList borderRadius="10px" minW="180px">
                        <MenuItem onClick={() => openPdf(invoice.id)}>
                          <HStack spacing={2}>
                            <Text fontSize="14px">
                              {isSimplified(invoice.invoiceType)
                                ? t("taxInvoices.viewReceipt")
                                : t("taxInvoices.viewInvoice")}
                            </Text>
                            <Icon boxSize={4}>
                              <ArrowTopRightOnSquareIcon />
                            </Icon>
                          </HStack>
                        </MenuItem>
                        {canUpgrade(invoice) && (
                          <MenuItem
                            onClick={() => setConfirmInvoiceId(invoice.id)}
                          >
                            <Text fontSize="14px">
                              {t("taxInvoices.requestCompleteInvoice")}
                            </Text>
                          </MenuItem>
                        )}
                      </MenuList>
                    </Menu>
                  </HStack>
                </HStack>
              </Fragment>
            ))}
            {hasMore ? (
              <Box
                w="full"
                borderTopWidth="1px"
                borderTopColor="slate.100"
                pt={4}
                mt={1}
              >
                <Button
                  w="full"
                  variant="senseiOutlined"
                  isLoading={fetchMoreLoading}
                  onClick={() => {
                    setFetchMoreLoading(true);
                    fetchMore({
                      variables: {
                        cursor: data?.taxInvoices.endCursor,
                      },
                      updateQuery: (previousResult, { fetchMoreResult }) => {
                        if (!fetchMoreResult) {
                          return previousResult;
                        }

                        return {
                          taxInvoices: {
                            ...fetchMoreResult.taxInvoices,
                            items: [
                              ...previousResult.taxInvoices.items,
                              ...fetchMoreResult.taxInvoices.items,
                            ],
                          },
                        };
                      },
                    }).finally(() => {
                      setFetchMoreLoading(false);
                    });
                  }}
                >
                  {t("taxInvoices.loadMore")}
                </Button>
              </Box>
            ) : null}
          </VStack>
        ) : (
          <Box textAlign="center" py={5} w="full">
            <Text
              fontFamily="'Barlow Semi Condensed', sans-serif"
              fontWeight={600}
              fontSize="15px"
              color="slate.600"
              mb={1}
            >
              {t("taxInvoices.noInvoices")}
            </Text>
          </Box>
        )}
      </VStack>
      </Box>

      <RequestInvoiceModal
        isOpen={!!confirmInvoiceId}
        onClose={() => setConfirmInvoiceId(null)}
        onSuccess={() => refetch()}
        invoiceId={confirmInvoiceId}
      />
    </Box>
  );
};
